import { MIDASProject, ColumnId, createDatasetId, asColumnNames } from '../../types';
import { useReports } from './useReports';
import {
  createHistogramElement,
  createScatterPlotElement,
  createTimeSeriesPlotElement,
  createMultiTimeSeriesPlotElement,
  createStatisticsSummaryElement
} from '../utils/reportElementFactory';
import { checkReportNameDuplicateHelper } from '../utils/reportUtils';

/**
 * useProjectReportManager - backward compatibility wrapper
 *
 * @deprecated Use useReports directly in new code
 */
export function useProjectReportManager({
  project,
  onProjectUpdate
}: {
  project: MIDASProject | null;
  onProjectUpdate: (updatedProject: MIDASProject) => void;
}) {
  const {
    reports,
    selectedReportId,
    createReport,
    updateReport,
    deleteReport,
    addElement,
    updateElement,
    removeElement,
    createHistogramReport,
    createScatterPlotReport,
    createTimeSeriesPlotReport,
    createMultiTimeSeriesPlotReport,
    createStatisticsSummaryReport,
    setSelectedReportId
  } = useReports({ project, onProjectUpdate });

  return {
    reports,
    selectedReportId,

    // Report creation
    createEmptyReport: createReport,
    createReportWithHistogram: async (
      columnId: ColumnId,
      bins: number = 10,
      reportName?: string
    ) => {
      if (!project || !project.datasets) return undefined;
      const datasetIds = Object.keys(project.datasets);
      if (datasetIds.length === 0) return undefined;
      const dataset = project.datasets[createDatasetId(datasetIds[0])];
      return createHistogramReport(columnId, dataset, bins, reportName);
    },

    addHistogramToReport: async (
      reportId: string,
      columnId: ColumnId,
      bins: number = 10,
      datasetId?: string
    ) => {
      if (!project?.datasets) return;
      const activeDataset = datasetId ?
        project.datasets[createDatasetId(datasetId)] :
        project.datasets[createDatasetId(Object.keys(project.datasets)[0])];
      if (!activeDataset) return;

      const element = createHistogramElement(columnId, activeDataset, bins);
      await addElement(reportId, element);
    },

    addScatterPlotToReport: async (
      reportId: string,
      xColumn: ColumnId,
      yColumn: ColumnId,
      datasetId?: string
    ) => {
      if (!project?.datasets) return;
      const activeDataset = datasetId ?
        project.datasets[createDatasetId(datasetId)] :
        project.datasets[createDatasetId(Object.keys(project.datasets)[0])];
      if (!activeDataset) return;

      const element = createScatterPlotElement(xColumn, yColumn, activeDataset);
      await addElement(reportId, element);
    },

    createReportWithScatterPlot: async (
      xColumn: ColumnId,
      yColumn: ColumnId,
      reportName?: string
    ) => {
      if (!project || !project.datasets) return;
      const datasetIds = Object.keys(project.datasets);
      if (datasetIds.length === 0) return;
      const dataset = project.datasets[createDatasetId(datasetIds[0])];
      return createScatterPlotReport(xColumn, yColumn, dataset, reportName);
    },

    addTimeSeriesPlotToReport: async (
      reportId: string,
      dateColumn: ColumnId,
      valueColumn: ColumnId
    ) => {
      if (!project?.datasets) return;
      const datasetIds = Object.keys(project.datasets);
      if (datasetIds.length === 0) return;
      const activeDataset = project.datasets[createDatasetId(datasetIds[0])];

      const element = createTimeSeriesPlotElement(dateColumn, valueColumn, activeDataset);
      await addElement(reportId, element);
    },

    createReportWithTimeSeriesPlot: async (
      dateColumn: ColumnId,
      valueColumn: ColumnId,
      reportName?: string
    ) => {
      if (!project || !project.datasets) return undefined;
      const datasetIds = Object.keys(project.datasets);
      if (datasetIds.length === 0) return undefined;
      const dataset = project.datasets[createDatasetId(datasetIds[0])];

      return createTimeSeriesPlotReport(dateColumn, valueColumn, dataset, reportName);
    },

    addMultiTimeSeriesPlotToReport: async (
      reportId: string,
      dateColumn: ColumnId,
      valueColumns: ColumnId[]
    ) => {
      if (!project?.datasets) return;
      const datasetIds = Object.keys(project.datasets);
      if (datasetIds.length === 0) return;
      const activeDataset = project.datasets[createDatasetId(datasetIds[0])];

      const element = createMultiTimeSeriesPlotElement(dateColumn, valueColumns, activeDataset);
      await addElement(reportId, element);
    },

    createReportWithMultiTimeSeriesPlot: async (
      dateColumn: ColumnId,
      valueColumns: ColumnId[],
      reportName?: string
    ) => {
      if (!project || !project.datasets) return undefined;
      const datasetIds = Object.keys(project.datasets);
      if (datasetIds.length === 0) return undefined;
      const dataset = project.datasets[createDatasetId(datasetIds[0])];

      return createMultiTimeSeriesPlotReport(dateColumn, valueColumns, dataset, reportName);
    },

    addStatisticsSummaryToReport: async (
      reportId: string,
      targetColumns: string[],
      datasetId: string
    ) => {
      const element = createStatisticsSummaryElement(asColumnNames(targetColumns), datasetId);
      await addElement(reportId, element);
    },

    createReportWithStatisticsSummary: async (
      targetColumns: string[],
      datasetId: string,
      reportName?: string
    ) => {
      return createStatisticsSummaryReport(targetColumns, datasetId, reportName);
    },

    // Markdown content update
    updateContent: async (reportId: string, content: string) => {
      await updateReport(reportId, { content });
    },

    updateReportElement: updateElement,
    updateReportName: (reportId: string, newName: string) =>
      updateReport(reportId, { name: newName }),
    selectReport: (reportId: string) => {
      setSelectedReportId(reportId);
    },
    deleteReport,
    deleteReportElement: removeElement,
    addElement,

    checkReportNameDuplicateHelper: (name: string) =>
      checkReportNameDuplicateHelper(name, project)
  };
}
